/**
 * Ячейки таблицы размеров — общие для карточки товара и её планового ввода.
 *
 * Общими стали ЯЧЕЙКИ, а не таблица целиком: планирование живёт теперь на
 * карточке (было отдельным экраном «Набор плана», удалён 13.09.26 вместе со
 * своей страницей — планирование переехало сюда). Компонент на все случаи
 * пришлось бы делать настолько настраиваемым, что он вышел бы длиннее
 * простых.
 *
 * А вот две копии ПОДСКАЗКИ разъехались бы на первой же правке, и спорящие
 * подписи на двух экранах читаются как разные числа. Поэтому подписи,
 * подсказки и правило подсветки живут в `lib/product/cellTitles.ts`, а здесь —
 * только разметка.
 *
 * ВЕРТИКАЛЬНЫХ ЛИНЕЕК В ЯЧЕЙКАХ НЕТ (11.09.26, решение владельца — тем же
 * замечанием, каким их сняли с витрины: «цифры лучше читаются, когда нет этих
 * разделителей»). Границы остались только горизонтальные: строку глаз ведёт по
 * линии, а столбец собирают выровненные числа и воздух между ними.
 *
 * Вертикальных линий осталось ДВЕ, и обе отделяют «своё» от «поразмерного» —
 * подпись строки от «итога» и «итог» от колонок размеров. Ровно та же
 * расстановка, что на витрине: там подпись, «всего» и лента периодов.
 *
 * ПРАВКА ТРОНУЛА ДВА ЭКРАНА СРАЗУ, и это верно, а не побочно: таблица здесь
 * одна на оба, и второе её поведение стало бы вторым местом правды о виде.
 *
 * Здесь — ячейки остатков, итогов, коробов и поле ввода; ячейки продаж — в
 * `salesCells.tsx`. Прежний модуль `components/sizeCells.tsx` — фасад.
 */

import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";

import { Link } from "react-router";

import { nf, pct } from "@/lib/format";
import { stale, timeOf } from "@/lib/product/cellTitles";
import { type ZeroShare, zeroCell } from "@/lib/productSizes";
import { клавишаКнопки } from "@/lib/selection";

/** КОЛОНКИ «ИТОГ» НЕТ, КОГДА РАЗМЕР ОДИН (разметка 25.09.26 15:22, п.2: «если
 * есть один только размер, то не нужно и „итог“ и „ноль“ показывать»). Итог
 * одного размера равен ему число в число — то же правило, по которому строка
 * WB, повторяющая итог площадки, не рисуется. Контекстом, а не пропом каждой
 * строки: строк в таблице два десятка, и забытая одна сдвинула бы колонки. */
export const HideTotal = createContext(false);

export function Row({
  label,
  total,
  children,
  annot,
  inset,
  onToggle,
  expanded,
  className,
}: {
  /** Обычно строка, но «в планах» кладёт сюда треугольник раскрытия рядом с
   * подписью — второй такой строки-заголовка заводить не стали. */
  label: ReactNode;
  /** Итоговая ячейка — ПЕРВАЯ колонка строки. Без неё колонки итога нет. */
  total?: React.ReactNode;
  children: React.ReactNode;
  /** Имя строки для разметки. Ставится там, где подписи ПОВТОРЯЮТСЯ: «на
   * складе» есть и у FBW, и у FBO, а адрес замечания собирается по атрибутам
   * `data-annot` вверх по дереву — без имени он опознаёт строку текстом ячейки,
   * то есть сразу обе. */
  annot?: string;
  /** Подстрочник: строка РАСКЛАДЫВАЕТ ту, что над ней, а не продолжает таблицу
   * вниз. Отступ тот же приём, каким на витрине стоят доли под своими суммами:
   * глаз читает «продадим 14, из них WB 9» одним движением. */
  inset?: boolean;
  /** Делает ВСЮ ячейку подписи кликабельной — не только `label` внутри неё.
   * Тот же довод, что у `SectionHead` в витрине (12.09.26): попадать курсором
   * в треугольник 10×10 неудобно, а промах читается как «не работает». В
   * отличие от `SectionHead` клавиатура здесь ЕСТЬ — `role="button"` без неё
   * был бы враньём для скринридера. */
  onToggle?: () => void;
  /** `aria-expanded` ячейки — только когда она интерактивна. */
  expanded?: boolean;
  /** Классы строки. Вид «список» раздела «Товары» ставит сюда толстую линию над
   * первой строкой группы: строк-заголовков у него нет, они растили бы высоту. */
  className?: string;
}) {
  const безИтога = useContext(HideTotal);
  return (
    <tr data-annot={annot} className={className}>
      <td
        className={`whitespace-nowrap border-b border-r border-border bg-bg-subtle px-1.5 py-1 text-[11px] text-fg-muted${
          inset ? " pl-5" : ""
        }${onToggle ? " cursor-pointer hover:bg-bg-hover" : ""}`}
        {...(onToggle
          ? {
              role: "button" as const,
              tabIndex: 0,
              "aria-expanded": expanded,
              onClick: onToggle,
              onKeyDown: клавишаКнопки(onToggle),
            }
          : {})}
      >
        {label}
      </td>
      {безИтога ? null : total}
      {children}
    </tr>
  );
}

/** Итог строки по всем размерам.
 *
 * Считается НА ФРОНТЕ из тех же ячеек, что нарисованы рядом: итог, приехавший
 * отдельным полем с сервера, однажды разошёлся бы со строками, и объяснить
 * это было бы нечем.
 */
export function TotalCell({
  value,
  onOpen,
  tone = "",
}: {
  value: number | null;
  onOpen?: () => void;
  /** Цвет числа — у строк, которые обязаны бросаться в глаза («в коробах»). */
  tone?: string;
}) {
  const { className = "", ...rest } = reportCellProps(onOpen);
  return (
    <td
      className={`min-w-[62px] border-b border-r border-border bg-bg-subtle px-1.5 py-1 text-right font-medium tabular-nums ${tone} ${className}`}
      {...rest}
    >
      {value === null ? (
        <span className="text-fg-faint">—</span>
      ) : (
        nf.format(value)
      )}
    </td>
  );
}

/** Процент в итоге ПЕРЕСЧИТАН из итоговых чисел, а не усреднён по размерам:
 * среднее из процентов дало бы третье число, не равное ни одному источнику. */
export function TotalPctCell({
  value,
  onOpen,
}: {
  value: number | null;
  onOpen?: () => void;
}) {
  const { className = "", ...rest } = reportCellProps(onOpen);
  return (
    <td
      className={`border-b border-r border-border bg-bg-subtle px-1.5 py-1 text-right font-medium tabular-nums text-fg-muted ${className}`}
      {...rest}
    >
      {value === null ? <span className="text-fg-faint">—</span> : pct(value)}
    </td>
  );
}

/** Сумма по размерам. Если слагаемых не нашлось вовсе — `null`, а не ноль: у
 * FBW `null` значит «строки в кэше WB нет», и нулём это писать нельзя. */
export function sumOf<T>(
  items: T[],
  pick: (item: T) => number | null | undefined,
): number | null {
  let sum = 0;
  let seen = false;
  for (const item of items) {
    const value = pick(item);
    if (value === null || value === undefined) continue;
    seen = true;
    sum += value;
  }
  return seen ? sum : null;
}

/** Итоговая ячейка строки «в планах» и её строк-дат — ссылка на «Планы»,
 * отобранные этим же количеством (решение 2 спеки «Планирование переезжает в
 * карточку товара»). Ноль ссылкой не становится: открывать по нему нечего, а
 * подчёркнутый ноль обещал бы пустой экран. Классы — те же, что у `TotalCell`,
 * потому что ссылка не обязана менять вид ячейки. */
export function PlanTotalCell({
  value,
  href,
  title,
}: {
  value: number;
  href: string;
  title: string;
}) {
  return (
    <td className="min-w-[62px] border-b border-r border-border bg-bg-subtle px-1.5 py-1 text-right font-medium tabular-nums">
      {value > 0 ? (
        <Link to={href} className="hover:underline" title={title}>
          {nf.format(value)}
        </Link>
      ) : (
        nf.format(value)
      )}
    </td>
  );
}

/** Шапка колонки размера.
 *
 * `min-w-[64px]` — РОВНО ширина колонки витрины (12.09.26). До этого дня стояло
 * 54, и на карточке колонки всё равно выходили шире: слак `w-full`-таблицы
 * доставался им, а не хвосту. Число здесь и там одно, потому что таблицы стоят
 * половинами одного экрана: две разные ширины одинаковых на вид столбцов видны
 * каждый раз.
 *
 * Подпись прижата ВПРАВО вместе с числами под ней: столбец собирают выровненные
 * числа, а не линейка — её сняли 11.09.26.
 */
/**
 * Колонка размера.
 *
 * `deleted` — размер СНЯТ в CRM. Такой столбец доезжает только у товара, где
 * живых карточек не осталось вовсе: сервер отсекает удалённые
 * (`catalog.alive_only`), но пустая таблица соврала бы сильнее лишних строк —
 * по ней не понять, сняли товар или отвалился кабинет. Помечаем приглушённым
 * именем и подсказкой, а не значком: столбец узкий (64 px), и значок съел бы
 * место у самого имени.
 */
export function SizeHead({ size, edge, deleted }: { size: string; edge?: boolean; deleted?: boolean }) {
  return (
    <th
      className={`min-w-[64px] border-b border-border bg-bg-subtle px-1.5 py-1 text-right font-medium${
        edge ? " border-r" : ""
      }${deleted ? " text-fg-muted line-through" : ""}`}
      title={deleted ? "размер удалён в CRM" : undefined}
    >
      {size}
    </th>
  );
}


/**
 * Поле ввода одного размера.
 *
 * Своё состояние, а не значение из ответа: набирают быстрее, чем отвечает
 * сеть, и подстановка серверного числа в открытое поле стирала бы набранное
 * под пальцами. Наверх уходит только то, что человек ДОПЕЧАТАЛ, — на уходе из
 * поля либо по Enter.
 *
 * Tab по полям идёт сам собой в порядке DOM: размеры слева направо, а за
 * последним — дата партии под таблицей (товар на карточке один).
 *
 * ЖИВЁТ ЗДЕСЬ, А НЕ В ЭКРАНЕ: поле делит модуль с `SizeHead`, чью ширину
 * колонки оно повторяет числом (64 px) — переезд в страницу развёл бы два
 * места правды о том, сколько пикселей у колонки размера, а ширина здесь
 * куплена поломкой вёрстки (ниже).
 */
export function QtyInput({
  value,
  disabled,
  broken,
  struck = false,
  reason,
  onCommit,
}: {
  value: number | null;
  disabled: boolean;
  broken: string | undefined;
  /** Число зачёркнуто: строка плана отменена или пересорт — его не ждём. */
  struck?: boolean;
  /** Почему поле закрыто, подсказкой — без красного: это не ошибка, а статус строки. */
  reason?: string | undefined;
  onCommit: (raw: string) => void;
}) {
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  const touched = useRef(false);

  useEffect(() => {
    if (!touched.current) setDraft(value === null ? "" : String(value));
  }, [value]);

  const commit = () => {
    if (!touched.current) return;
    touched.current = false;
    onCommit(draft.trim());
  };

  return (
    <div>
      <input
        inputMode="numeric"
        disabled={disabled}
        value={draft}
        title={broken ?? reason}
        onChange={(event) => {
          touched.current = true;
          setDraft(event.target.value);
        }}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === "Enter") (event.target as HTMLInputElement).blur();
        }}
        // ПРИЖАТО ВПРАВО ВМЕСТЕ СО СТОЛБЦОМ (12.09.26): числа таблицы размеров
        // с этого дня стоят у правого края, и поле, оставшись по центру,
        // разошлось бы с остатком и коробами в той же колонке.
        //
        // ШИРИНА — ЧИСЛОМ, И ИМЕННО 64 px: ровно колонка размера (`SizeHead`),
        // чтобы правый край поля совпал с краем столбца. `w-full` тут нельзя —
        // ячейка таблицы просит 100 % ШИРИНЫ ТАБЛИЦЫ, и на `w-max`-таблице
        // колонка раздувается с 64 px до 196 (поймано боевым замером).
        className={`h-[30px] w-[64px] bg-transparent pr-1.5 pl-1 text-right text-[13px] tabular-nums outline-none focus:bg-bg-hover disabled:opacity-50 ${
          broken ? "text-err" : ""
        } ${value ? "font-semibold" : ""} ${struck ? "line-through" : ""}`}
      />
    </div>
  );
}

/** Оранжевый «в коробах» (разметка владельца 25.09.26): то, что лежит в
 * коробах, не должно теряться глазом среди остатков и планов. Красится подпись,
 * итог и ненулевые ячейки; нули остаются бледными. */
export const KOROB_TONE = "font-medium text-warn";

export function KorobLabel({ total }: { total: number | null }) {
  return total ? <span className={KOROB_TONE}>в коробах</span> : "в коробах";
}

export function KorobCell({ korob }: { korob: number }) {
  return (
    <td className={`border-b border-border px-1.5 py-1 text-right tabular-nums${korob ? ` ${KOROB_TONE}` : ""}`}>
      {korob ? nf.format(korob) : <span className="text-fg-faint">0</span>}
    </td>
  );
}

/** Одно из состояний остатка на складах WB: лежит · едет к клиенту · едет
 * обратно. Три состояния — три строки, потому что говорят они разное: первое
 * ждёт, второе уже уехало, третье вернётся. */
/**
 * Ячейка строки «без наличия» (разметка 29.09.26 08:10 п.1) — ОДНА на список и
 * карточку: доля времени, когда размера не было на складе площадки. Всегда был
 * — ячейка ПУСТА; журнала нет — прочерк; процент без подсветки.
 */
export function ZeroCell({ value, place }: { value: ZeroShare | null | undefined; place: string }) {
  const cell = zeroCell(value);
  const title = value
    ? `без наличия на складе ${place} ${value.pct.toLocaleString("ru-RU")} % времени — среднее по ${value.days} дн. журнала`
    : `журнала остатка ${place} по размеру нет`;
  return (
    <td title={title} className="border-b border-border px-1.5 py-1 text-right tabular-nums">
      {cell === null ? null : cell.dash ? <span className="text-fg-faint">{cell.text}</span> : cell.text}
    </td>
  );
}

/** Пустая ячейка итога строки «без наличия»: процент в итог не складывается. */
export function ZeroTotalCell() {
  return (
    <td
      className="min-w-[62px] border-b border-r border-border bg-bg-subtle px-1.5 py-1"
      title="процент в итог не складывается"
    />
  );
}

export function StockCell({
  value,
  syncedAt,
  hint,
  empty = "строки в кэше WB нет — размер там не заводился",
}: {
  /** null — строки остатка нет вовсе; это НЕ ноль на складе. */
  value: number | null;
  syncedAt: string | null;
  hint: string;
  /** Что значит прочерк ИМЕННО У ЭТОЙ площадки: у WB и Ozon разные кэши и
   * разные причины пустоты, и общие слова были бы неточными. */
  empty?: string;
}) {
  return (
    <td
      title={value === null ? empty : `${hint} · снимок ${timeOf(syncedAt)}`}
      className={`border-b border-border px-1.5 py-1 text-right tabular-nums ${
        stale(syncedAt) ? "text-warn" : ""
      }`}
    >
      {value === null ? (
        <span className="text-fg-faint">—</span>
      ) : (
        nf.format(value)
      )}
    </td>
  );
}

/** Ячейки блока финотчёта КЛИКАБЕЛЬНЫ: клик открывает ряд выкупаемости.
 *
 * Ячейки блока заказов остаются немыми, и это не забывчивость: панель рисует
 * финотчёт, и клик по проценту заказов открыл бы график, который с этим
 * процентом не сходится. Ровно та путаница, ради которой строка «выкуплено»
 * стала чистой. */
export function reportCellProps(onOpen: (() => void) | undefined) {
  if (!onOpen) return {};
  return {
    onClick: onOpen,
    role: "button" as const,
    tabIndex: 0,
    onKeyDown: (event: ReactKeyboardEvent<HTMLTableCellElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        onOpen();
      }
    },
    className: "cursor-pointer hover:bg-bg-hover",
  };
}
